import {
  colorAccessor,
  heightAccessor,
  widthAccessor,
  xAccessor,
  yAccessor,
} from './rect-accessors.js';
import { BarCanvas } from '../../../core/components/canvas/components/BarCanvas.js';
import { useGridPlotSize } from '../../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { DEFAULT_FILL_OPACITY } from '../../../core/constants.js';
import { safeGet } from '../../../core/utils/safe-get.js';
import { RECT_STROKE_WIDTH } from '../../constants/defaults.js';
import type { AxisIdToScales } from '../../context/XYChartScales.context.js';
import { useXYChartColorScales } from '../../hooks/useXYChartColorScales.js';
import { useXYChartData } from '../../hooks/useXYChartData.js';
import type {
  RectDatapointInternal,
  RectSeriesWithDatapoints,
} from '../../types/xy-chart-internals.js';
import { getCurrentScale } from '../../utils/get-scale.js';
import { isGapDatapointForDisplay } from '../../utils/validation-datapoint.js';
import { relAccessor } from '../../utils/value-representation.js';

export function RectDisplay(props: {
  axisIdToScales: AxisIdToScales;
  series: RectSeriesWithDatapoints[];
  rectFilterCallback?: (d: RectDatapointInternal) => boolean;
}) {
  const { axisIdToScales, series, rectFilterCallback } = props;
  const { height, width } = useGridPlotSize();
  const colorScalesMap = useXYChartColorScales();

  const { datapointMetadataMap, axisIdToScaleDomain, seriesToMetadata } =
    useXYChartData();

  return series.map((series) => {
    const xScale = getCurrentScale(axisIdToScales, series.xAxisId);
    const yScale = getCurrentScale(axisIdToScales, series.yAxisId);
    const { xAxis, yAxis } = safeGet(seriesToMetadata, series);
    const xRelAccessor = relAccessor(xAxis, axisIdToScaleDomain);
    const yRelAccessor = relAccessor(yAxis, axisIdToScaleDomain);
    const colorScale = colorScalesMap.get(series.id);

    return colorScale ? (
      <BarCanvas
        key={series.id}
        data={
          rectFilterCallback
            ? series.filteredData.filter(rectFilterCallback)
            : series.filteredData
        }
        canvasHeight={height}
        canvasWidth={width}
        color={colorAccessor(colorScale, datapointMetadataMap)}
        x={xAccessor(xScale, xRelAccessor)}
        width={widthAccessor(xScale, xRelAccessor)}
        y={yAccessor(yScale, yRelAccessor)}
        height={heightAccessor(yScale, yRelAccessor)}
        opacity={() => DEFAULT_FILL_OPACITY}
        strokeWidth={RECT_STROKE_WIDTH}
        defined={isGapDatapointForDisplay(datapointMetadataMap)}
      />
    ) : null;
  });
}

RectDisplay['displayName'] = 'RectDisplay';
